import { el, clear } from '../dom.js';
import { t } from '../i18n.js';

// The link list, used at both levels the data model has: on the series for
// what stays the same every week, and on a single date for the guest who is
// only there that night. Same control, same payload shape, different owner —
// which is exactly why it is one component and not two forms.

export const MAX_LINKS = 6;

export function linkField({ initial = [], label = t.fieldLinks, idPrefix = 'lf' } = {}) {
  let rows = initial.map((link) => ({ label: link.label ?? '', url: link.url ?? '' }));

  const list = el('ul', { class: 'link-rows' });
  const add = el('button', { class: 'button button-quiet', type: 'button' }, t.addLink);
  const hint = el('p', { class: 'card-meta', id: `${idPrefix}-hint` }, t.linkHint);

  const node = el('fieldset', { class: 'field link-field' }, [
    el('legend', {}, label),
    list,
    add,
    hint,
  ]);

  add.addEventListener('click', () => {
    rows.push({ label: '', url: '' });
    paint({ focusLast: true });
  });

  paint();

  function paint({ focusLast = false } = {}) {
    clear(list);
    rows.forEach((row, index) => list.append(rowNode(row, index)));
    // The cap is enforced in the db layer for every caller; hiding the button
    // is what stops a publisher discovering it as a 422 after typing.
    add.hidden = rows.length >= MAX_LINKS;
    if (focusLast) list.querySelector('li:last-child input')?.focus();
  }

  function rowNode(row, index) {
    const labelId = `${idPrefix}-label-${index}`;
    const urlId = `${idPrefix}-url-${index}`;

    const labelInput = el('input', {
      id: labelId, type: 'text', autocomplete: 'off', 'aria-label': t.linkLabel,
      placeholder: t.linkLabel,
    });
    labelInput.value = row.label;
    labelInput.addEventListener('input', () => { rows[index].label = labelInput.value; });

    const urlInput = el('input', {
      id: urlId, type: 'url', 'aria-label': t.linkUrl, placeholder: 'https://',
      'aria-describedby': `${idPrefix}-hint`,
    });
    urlInput.value = row.url;
    urlInput.addEventListener('input', () => { rows[index].url = urlInput.value; });

    const remove = el('button', {
      class: 'button button-quiet', type: 'button', 'aria-label': t.removeLink,
    }, '×');
    remove.addEventListener('click', () => {
      rows.splice(index, 1);
      paint();
    });

    return el('li', { class: 'link-row' }, [labelInput, urlInput, remove]);
  }

  // Empty rows are dropped rather than refused: somebody who clicked "add" and
  // changed their mind has not made a mistake worth an error message.
  function value() {
    return rows
      .map((row) => ({ label: row.label.trim(), url: row.url.trim() }))
      .filter((row) => row.url);
  }

  return { node, value };
}

// The 422s this control can produce, worded for the person who typed the URL.
export const LINK_MESSAGES = {
  bad_url: t.linkBadUrl,
  url_required: t.linkBadUrl,
  url_too_long: t.linkBadUrl,
  label_too_long: t.linkBadUrl,
  too_many: t.linkTooMany,
};
